import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { RentalLifecycle, Sales_order_l, lineNetTaxx, roundHalfAway } from '@app/shared/_models/sales';
import { BillingTriggerEvent, Product, RentalTerms, RtLinkedProduct } from '@app/shared/_models/product';
import { C_CUF_CONFIGURATION, C_CUF_CONFIGURATION_VALUE, OrderLineType } from '@app/shared/constants';
import { round_currency } from '@app/shared/util';

const CONF = C_CUF_CONFIGURATION.toString();

// Copie de RentgeneralComponent.highestSeq : les seq des composants d'une configuration comptent aussi.
export function highestSeq(lines: FormArray): number {
  let maxSeq = 0;
  for (const line of lines?.controls ?? []) {
    const seq = line.get('seq')?.value ?? 0;
    if (typeof seq === 'number' && seq > maxSeq) maxSeq = seq;
    const conf = line.value?.data?.cufs?.[C_CUF_CONFIGURATION_VALUE];
    if (Array.isArray(conf)) for (const c of conf) maxSeq = Math.max(maxSeq, c.seq);
  }
  return maxSeq;
}

export function isRentalTermInError(rt: RentalTerms): boolean {
  if (rt.error) return true;
  return rt.tiered_pricing?.some((tp) => !!tp.fse_lpp && tp.tarifref == null) ?? false;
}

// Configurable par son modèle ou par lui-même : la configuration ne vit que dans l'écran actuel.
export function isConfigurableProduct(p: Product | null | undefined): boolean {
  return !!p?.lnk_template?.data?.cufs?.hasOwnProperty(CONF) || !!p?.data?.cufs?.hasOwnProperty(CONF);
}

export function isConfigurableLine(line: any): boolean {
  return !!line?.data?.cufs?.[CONF];
}

export function linkedProductsFor(rt: { linked_products?: RtLinkedProduct[] } | null | undefined, event: BillingTriggerEvent): RtLinkedProduct[] {
  return (rt?.linked_products ?? []).filter((lp) => lp.billing_trigger?.event_name === event);
}

// TVA d'une location = celle de son contrat, jamais celle du modèle d'article (audit 31/10 #20 : un LPP à 5,5 % aurait été loué à 5,5 %).
export function rentalVat(rt: RentalTerms): { vat: number; vatRate: number } | string {
  const vat = rt.vat;
  const rawVatRate = rt.vat_rate;
  if (vat == null || rawVatRate == null) return `Le contrat « ${rt.name ?? 'sans nom'} » n'a pas de TVA : renseignez-la sur la fiche article (contrats de location) avant de livrer ce matériel`;
  const vatRate = typeof rawVatRate === 'number' ? rawVatRate : parseFloat(rawVatRate as any);
  if (isNaN(vatRate)) return `Le contrat « ${rt.name ?? 'sans nom'} » a un taux de TVA illisible : corrigez-le sur la fiche article`;
  return { vat, vatRate };
}

// Prix de base = première période du contrat, comme rentgeneral ; null quand le contrat n'a aucun palier (prix à saisir).
export function basePriceTaxx(rt: RentalTerms): number | null {
  const tp = rt.tiered_pricing?.find((t) => t.starting_period === 1) ?? rt.tiered_pricing?.[0];
  return tp ? Number(tp.uprice_taxx) || 0 : null;
}

export function ttcOf(taxx: number, vatRate: number): number {
  return round_currency(taxx * (1 + vatRate / 100));
}

export interface NewRentalInput {
  product: Product;
  terms: RentalTerms;
  vat: number;
  vatRate: number;
  memo: string;
  price: number;        // prix saisi, HT si le client est en HT, TTC sinon
  priceTouched: boolean;
  isHt: boolean;
  duration: number | null;  // vide avec « Sans limite »
  unlimited: boolean;
  ppv: number | null;
  seq: number;
}

// Forme exacte de RentgeneralComponent.selectRentalTerm ; un prix retouché suit onPriceTaxxChange/onPriceTaxiChange puis setRentalNet.
export function newRentalLine(fb: FormBuilder, p: NewRentalInput): FormGroup {
  const base = basePriceTaxx(p.terms);
  let taxx: number, taxi: number, netTaxx: number, netTaxi: number;
  if (!p.priceTouched && base !== null) {
    taxx = base;
    taxi = ttcOf(base, p.vatRate);
    netTaxx = taxx;
    netTaxi = taxi;
  } else {
    taxx = p.isHt ? p.price : p.vatRate ? round_currency(p.price / (1 + p.vatRate / 100)) : p.price;
    taxi = p.isHt ? ttcOf(p.price, p.vatRate) : p.price;
    netTaxx = lineNetTaxx(1, taxx);
    netTaxi = roundHalfAway(netTaxx * (1 + p.vatRate / 100), 2);
  }
  // Durée vide = sans limite : qty absente, comme la ligne neuve de rentgeneral (décision de JP du 26/10/2026).
  const rentalLifecycle: RentalLifecycle = { asset: null, qty: !p.unlimited && p.duration != null && p.duration > 0 ? p.duration : null };
  const data: any = p.product.data ? { ...p.product.data } : {};
  return fb.group({
    id: 0,
    id_order: 0,
    seq: p.seq,
    typ: OrderLineType.RENTAL,
    product: p.product.id,
    template: p.product.template,
    qty: 1,
    product_name: p.product.name,
    ppv: p.ppv,
    vat: p.vat,
    vat_rate: p.vatRate,
    uprice_taxx: new FormControl<number>(taxx),
    uprice_taxi: new FormControl<number>(taxi),
    unet_taxx: new FormControl<number>(taxx),
    unet_taxi: new FormControl<number>(taxi),
    vat_cat: new FormControl<string>('S'),
    net_taxx: new FormControl<number>(netTaxx),
    net_taxi: new FormControl<number>(netTaxi),
    spl: null,
    packaging_name: '',
    packaging_qtybaseuom: 1,
    data: new FormControl(data),
    memo: p.memo.trim(),
    rental_terms_snapshot: new FormControl(p.terms),
    rental_lifecycle: Sales_order_l.asRentalLifecycleFormGroup(rentalLifecycle),
  });
}

// --- « Reprise hors contrat » : matériel chez le patient qu'aucune ligne de location ne suit (JP 29/10) ---

export interface ReturnFormState { product: Product | null; memo: string; qty: number | null; detail: string; }

export function emptyReturnForm(memo = ''): ReturnFormState {
  return { product: null, memo, qty: 1, detail: '' };
}

// Une reprise hors contrat = UN matériel (ligne à −1, règle du serveur du 31/10) : plusieurs matériels font plusieurs reprises.
export function returnFormProblem(p: ReturnFormState | null): string | null {
  if (!p?.product) return "Choisissez l'article repris";
  if (p.product.lnk_template?.vat == null || p.product.lnk_template?.vat_rate == null) return "Pas de TVA définie sur l'article";
  if (Number(p.qty) !== 1) return 'Une reprise porte un seul matériel : saisissez-en une par matériel';
  return null;
}

// Préfixe de la reprise hors contrat (JP 30/09, venu de la F3) : un article déjà nommé « Reprise … » ne le prend pas deux fois.
const REPRISE_LABEL = 'Reprise';
export function repriseName(name: string | undefined): string | undefined {
  const lower = name?.toLowerCase() ?? '';
  const prefix = REPRISE_LABEL.toLowerCase();
  return !name || lower === prefix || lower.startsWith(prefix + ' ') ? name : `${REPRISE_LABEL} ${name}`;
}

export function outOfContractName(productName: string | undefined, detail: string): string {
  const d = detail.trim();
  return `${repriseName(productName) ?? 'Reprise'} · hors contrat${d ? ' · ' + d : ''}`;
}

// Ligne louée à −1 (un matériel repris), prix 0, aucun contrat, data.hors_contrat : le moteur ne la lit pas, seul son BL −1 existe (JP 30/10, serveur 31/10).
export function newOutOfContractReturnLine(fb: FormBuilder, p: ReturnFormState, ppv: number | null, seq: number): FormGroup {
  const product = p.product!;
  const vatRate = Number(product.lnk_template!.vat_rate) || 0;
  return fb.group({
    id: 0,
    id_order: 0,
    seq,
    typ: OrderLineType.RENTAL,
    product: product.id,
    template: product.template,
    qty: -1,
    product_name: outOfContractName(product.name, p.detail),
    ppv,
    vat: product.lnk_template!.vat,
    vat_rate: vatRate,
    uprice_taxx: new FormControl<number>(0),
    uprice_taxi: new FormControl<number>(0),
    unet_taxx: new FormControl<number>(0),
    unet_taxi: new FormControl<number>(0),
    vat_cat: new FormControl<string>('S'),
    net_taxx: new FormControl<number>(0),
    net_taxi: new FormControl<number>(0),
    spl: null,
    packaging_name: '',
    packaging_qtybaseuom: 1,
    data: new FormControl({ ...(product.data ?? {}), hors_contrat: true }),
    memo: p.memo.trim(),
    rental_terms_snapshot: new FormControl(null),
    rental_lifecycle: Sales_order_l.asRentalLifecycleFormGroup({ qty: null }),
  });
}

// Produit lié à la livraison, lu par /search/productpackaging (index jpid, tarif du client) au choix du contrat.
export interface LinkedSale {
  productId: number;
  quantity: number;
  ppv: any | null;
  name: string;
  problem: string | null;   // bloque l'enregistrement
  notSold: boolean;         // vendu quand même après confirmation, comme le NotSellingDialog de l'écran actuel
}

// Mêmes refus que QuotationgeneralComponent.addLine (is_product_sane, checkIsSold) ; prix à saisir et configuration restent à l'écran actuel.
export function linkedSaleOf(lp: RtLinkedProduct, ppv: any | null): LinkedSale {
  const p = ppv?.lnk_product;
  const base = { productId: lp.product_id, quantity: Math.abs(Number(lp.quantity) || 1), ppv, name: p?.name ?? `produit n° ${lp.product_id}`, notSold: false };
  if (!ppv?.product || !p?.lnk_template) return { ...base, ppv: null, problem: "introuvable dans l'index des produits" };
  if (p.lnk_template.vat == null || p.lnk_template.vat_rate == null) return { ...base, problem: 'pas de TVA définie sur le produit' };
  if (p.lnk_template.data?.cufs?.hasOwnProperty(CONF)) return { ...base, problem: "article configurable : livrez ce matériel depuis l'écran actuel" };
  const priced = (Number(p.price_taxi) || 0) > 0 || (Number(ppv.price_discounted_taxi) || 0) > 0 || (Number(p.price_taxx) || 0) > 0 || (Number(ppv.price_discounted_taxx) || 0) > 0;
  if (!priced) return { ...base, problem: "aucun prix : renseignez-le sur la fiche article ou livrez depuis l'écran actuel" };
  return { ...base, problem: null, notSold: !ppv.is_sellable || !p.is_sold };
}

// Prix d'UN conditionnement, remise du client comprise : calcul de QuotationgeneralComponent.addLine.
export function salePrices(ppv: any): { upriceTaxx: number; upriceTaxi: number; unetTaxx: number; unetTaxi: number; vatRate: number } {
  const p = ppv.lnk_product;
  const pack = parseFloat(ppv.qtybaseuom) || 1;
  return {
    upriceTaxx: roundHalfAway(Number(p.price_taxx) * pack, 4),
    upriceTaxi: roundHalfAway(Number(p.price_taxi) * pack, 4),
    unetTaxx: roundHalfAway(Number(ppv.price_discounted_taxx ? ppv.price_discounted_taxx : p.price_taxx) * pack, 4),
    unetTaxi: roundHalfAway(Number(ppv.price_discounted_taxi ? ppv.price_discounted_taxi : p.price_taxi) * pack, 4),
    vatRate: parseFloat(p.lnk_template.vat_rate) || 0,
  };
}

// Copie de QuotationgeneralComponent.getPackagingName : « Unité », « Carton de 4 Paquets » (uoms = c_values 1).
export function packagingNameOf(ppv: any, uoms: { id: number; name: string }[]): string {
  const unit = uoms?.find((u) => u.id == ppv?.packaging);
  if (!unit) return '';
  const baseUom = ppv.lnk_product?.lnk_template?.baseuom;
  const base = baseUom && baseUom != ppv.packaging ? uoms.find((u) => u.id == baseUom) : undefined;
  return unit.name + (ppv.qtybaseuom && ppv.qtybaseuom > 1 && base ? ` de ${ppv.qtybaseuom} ${base.name}s` : '');
}

// Ligne de vente (ITEM) de QuotationgeneralComponent.addLine, une par produit lié et sans fusion ; mémo du matériel pour lire le BL par patient.
export function newSaleLine(fb: FormBuilder, s: LinkedSale, seq: number, packagingName: string, memo: string): FormGroup {
  const ppv = s.ppv;
  const pr = salePrices(ppv);
  const netTaxx = lineNetTaxx(s.quantity, pr.unetTaxx);
  return fb.group({
    id: 0,
    id_order: 0,
    seq,
    typ: OrderLineType.ITEM,
    product: [ppv.product],
    template: [ppv.lnk_product.template],
    qty: [s.quantity],
    product_name: [ppv.lnk_product.name],
    ppv: [ppv.id],
    vat: [ppv.lnk_product.lnk_template.vat],
    vat_rate: [pr.vatRate],
    uprice_taxx: new FormControl<number>(pr.upriceTaxx),
    uprice_taxi: new FormControl<number>(pr.upriceTaxi),
    unet_taxx: new FormControl<number>(pr.unetTaxx),
    unet_taxi: new FormControl<number>(pr.unetTaxi),
    net_taxx: new FormControl<number>(netTaxx),
    net_taxi: new FormControl<number>(roundHalfAway(netTaxx * (1 + pr.vatRate / 100), 2)),
    spl: [ppv.lnk_product.spl],
    packaging_name: [packagingName],
    packaging_qtybaseuom: [parseFloat(ppv.qtybaseuom)],
    data: null,
    memo: [memo.trim()],
    ecoparts: [ppv.lnk_product.lnk_ecoparts],
  });
}

// --- « Livrer un matériel » : carte partagée par la page du dossier et la vue du payeur ---

export interface LinkedState { loading: boolean; items: LinkedSale[]; uoms: { id: number; name: string }[]; }

export interface AddFormState {
  product: Product | null;
  terms: RentalTerms | null;
  vat: number | null;
  vatRate: number;
  hasTier: boolean;
  memo: string;
  price: number | null;
  priceTouched: boolean;
  duration: number | null;
  unlimited: boolean;
  linked: LinkedState | null;   // produits liés à la livraison du contrat choisi, null sans produit lié
}

// Durée vide et « Sans limite » coché d'office : une ligne neuve n'a pas de Durée maximale (décision de JP du 26/10/2026).
export function emptyAddForm(memo = ''): AddFormState {
  return { product: null, terms: null, vat: null, vatRate: 0, hasTier: false, memo, price: null, priceTouched: false, duration: null, unlimited: true, linked: null };
}

export function addFormProblem(p: AddFormState | null): string | null {
  if (!p?.product || !p.terms || p.vat == null) return 'Choisissez un article puis un contrat';
  const price = p.price == null || (p.price as any) === '' ? NaN : Number(p.price);
  if (!Number.isFinite(price) || price < 0 || (!p.hasTier && price <= 0)) {
    return p.hasTier ? 'Prix par période invalide' : "Ce contrat n'a pas de tarif : saisissez le prix par période (supérieur à 0)";
  }
  if (!p.unlimited && !(p.duration != null && (p.duration as any) !== '' && Number.isInteger(Number(p.duration)) && Number(p.duration) >= 1)) {
    return 'Durée : un nombre entier de périodes, au moins 1 (ou « Sans limite »)';
  }
  if (p.linked?.loading) return 'Lecture des produits liés à la livraison…';
  const bad = p.linked?.items.find((it) => it.problem);
  if (bad) return `Produit lié ${bad.name} : ${bad.problem}`;
  return null;
}

// Le matériel au dossier ; ses produits liés à la livraison rendus à part, sous le même mémo : ils vont dans une commande de vente liée (JP 30/10).
export function appendAddedLines(fb: FormBuilder, lines: FormArray, p: AddFormState, isHt: boolean, ppv: number | null): FormGroup[] {
  lines.push(newRentalLine(fb, {
    product: p.product!, terms: p.terms!, vat: p.vat!, vatRate: p.vatRate, memo: p.memo ?? '', price: Number(p.price), priceTouched: p.priceTouched,
    isHt, duration: p.unlimited ? null : Number(p.duration), unlimited: p.unlimited, ppv, seq: highestSeq(lines) + 1,
  }));
  return (p.linked?.items ?? []).map((it, i) => newSaleLine(fb, it, i + 1, packagingNameOf(it.ppv, p.linked!.uoms), p.memo ?? ''));
}
